/**
 * Filtro multiselect por estado en la vista calendario agregada.
 * Combo con panel: cada estado con los mismos colores que los indicadores del calendario.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { tokens } from '../../../design-system/tokens';
import type { CalendarViewBooking } from '../calendarView.types';
import { getStatusColors } from './BookingIndicator';
import {
  CALENDAR_VIEW_STATUS_LABEL,
  CALENDAR_VIEW_STATUS_ORDER,
  type CalendarViewFilterableStatus,
} from './calendarViewStatusOptions';

// ============================================
// TYPES
// ============================================

export interface StatusFilterBarProps {
  selected: Set<CalendarViewFilterableStatus>;
  onToggle: (status: CalendarViewFilterableStatus) => void;
  onSelectAll: () => void;
}

// ============================================
// STYLES
// ============================================

const rootStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'flex-start',
  gap: tokens.spacing[2],
};

const dropdownAnchorStyle: CSSProperties = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
};

const labelStyle: CSSProperties = {
  fontSize: tokens.typography.fontSize.sm,
  fontWeight: tokens.typography.fontWeight.medium,
  color: tokens.colors.neutral[600],
  whiteSpace: 'nowrap',
  paddingTop: tokens.spacing[1],
};

const triggerStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: tokens.spacing[2],
  minWidth: 220,
  maxWidth: 360,
  minHeight: 36,
  padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
  border: `1px solid ${tokens.colors.neutral[300]}`,
  borderRadius: tokens.borderRadius.md,
  backgroundColor: tokens.colors.neutral[50],
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: tokens.typography.fontFamily.sans,
  transition: `background-color ${tokens.transitions.fast}, border-color ${tokens.transitions.fast}`,
};

const triggerChevron: CSSProperties = {
  flexShrink: 0,
  fontSize: 10,
  color: tokens.colors.neutral[500],
  marginLeft: 'auto',
};

const panelStyle: CSSProperties = {
  position: 'absolute',
  top: '100%',
  left: 0,
  marginTop: tokens.spacing[1],
  minWidth: 280,
  maxWidth: 360,
  maxHeight: 320,
  overflowY: 'auto',
  zIndex: 50,
  padding: tokens.spacing[2],
  borderRadius: tokens.borderRadius.md,
  border: `1px solid ${tokens.colors.neutral[200]}`,
  backgroundColor: tokens.colors.neutral[50],
  boxShadow: tokens.shadows.md,
};

const rowLabelStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: tokens.spacing[2],
  width: '100%',
  padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
  borderRadius: tokens.borderRadius.sm,
  cursor: 'pointer',
  fontSize: tokens.typography.fontSize.sm,
  userSelect: 'none',
};

const pillStyle = (colors: { background: string; text: string }): CSSProperties => ({
  display: 'inline-block',
  padding: `${tokens.spacing[0]} ${tokens.spacing[2]}`,
  borderRadius: tokens.borderRadius.full,
  backgroundColor: colors.background,
  color: colors.text,
  fontSize: tokens.typography.fontSize.xs,
  fontWeight: tokens.typography.fontWeight.medium,
  lineHeight: tokens.typography.lineHeight.tight,
  whiteSpace: 'nowrap',
});

// ============================================
// HELPERS
// ============================================

function isAllSelected(selected: Set<CalendarViewFilterableStatus>): boolean {
  return CALENDAR_VIEW_STATUS_ORDER.every((s) => selected.has(s));
}

function TriggerSummary({ selected }: { selected: Set<CalendarViewFilterableStatus> }) {
  if (isAllSelected(selected)) {
    return (
      <span style={{ fontSize: tokens.typography.fontSize.sm, color: tokens.colors.neutral[700] }}>
        Todos los estados
      </span>
    );
  }
  const ordered = CALENDAR_VIEW_STATUS_ORDER.filter((s) => selected.has(s));
  return (
    <span
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: tokens.spacing[1],
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
      }}
    >
      {ordered.map((status) => {
        const colors = getStatusColors(status as CalendarViewBooking['status']);
        return (
          <span key={status} style={pillStyle(colors)}>
            {CALENDAR_VIEW_STATUS_LABEL[status]}
          </span>
        );
      })}
    </span>
  );
}

// ============================================
// MAIN
// ============================================

export function StatusFilterBar({ selected, onToggle, onSelectAll }: StatusFilterBarProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const el = rootRef.current;
      if (el && !el.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const toggleRow = (status: CalendarViewFilterableStatus) => {
    onToggle(status);
  };

  const allSelected = isAllSelected(selected);

  return (
    <div ref={rootRef} style={rootStyle}>
      <span style={labelStyle} id="calendar-status-filter-label">
        Estado
      </span>
      <div style={dropdownAnchorStyle}>
        <button
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby="calendar-status-filter-label"
          onClick={() => setOpen((o) => !o)}
          style={triggerStyle}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = tokens.colors.neutral[100];
            e.currentTarget.style.borderColor = tokens.colors.neutral[400];
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = tokens.colors.neutral[50];
            e.currentTarget.style.borderColor = tokens.colors.neutral[300];
          }}
        >
          <TriggerSummary selected={selected} />
          <span style={triggerChevron} aria-hidden>
            {open ? '▲' : '▼'}
          </span>
        </button>

        {open ? (
          <div
            role="listbox"
            aria-multiselectable
            style={panelStyle}
            onMouseDown={(e) => e.preventDefault()}
          >
            <button
              type="button"
              onClick={() => {
                onSelectAll();
              }}
              style={{
                ...rowLabelStyle,
                border: 'none',
                background: allSelected ? tokens.colors.primary[100] : 'transparent',
                width: '100%',
                marginBottom: tokens.spacing[1],
                fontWeight: tokens.typography.fontWeight.semibold,
                color: tokens.colors.neutral[800],
              }}
            >
              Marcar todos
            </button>
            <div style={{ height: 1, backgroundColor: tokens.colors.neutral[200], marginBottom: tokens.spacing[1] }} />

            {CALENDAR_VIEW_STATUS_ORDER.map((status) => {
              const on = selected.has(status);
              const colors = getStatusColors(status as CalendarViewBooking['status']);
              const onlyOne = selected.size === 1 && on;
              return (
                <label
                  key={status}
                  style={{
                    ...rowLabelStyle,
                    opacity: onlyOne ? 0.65 : 1,
                    cursor: onlyOne ? 'not-allowed' : 'pointer',
                  }}
                  title={onlyOne ? 'Debe quedar al menos un estado activo' : undefined}
                >
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={onlyOne}
                    onChange={() => {
                      if (!onlyOne) toggleRow(status);
                    }}
                    style={{ width: 16, height: 16, cursor: onlyOne ? 'not-allowed' : 'pointer', flexShrink: 0 }}
                  />
                  <span style={pillStyle(colors)}>{CALENDAR_VIEW_STATUS_LABEL[status]}</span>
                </label>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default StatusFilterBar;
